import * as React from "react";
import { Image as ImageIcon, Upload } from "lucide-react";

import type { Theme } from "@/data/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/** Largest logo image we accept, matching the server-side cap. */
const MAX_LOGO_BYTES = 1024 * 1024; // 1 MB

export function LogoControl({
  theme,
  onChange,
}: {
  theme: Theme;
  onChange: (key: string, value: string | null) => void;
}) {
  const fileRef = React.useRef<HTMLInputElement>(null);
  const [error, setError] = React.useState<string | null>(null);

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    // Allow re-picking the same file again later by clearing the input value.
    e.target.value = "";
    if (!f) return;
    if (f.size > MAX_LOGO_BYTES) {
      setError("That image is too large — keep it under 1 MB.");
      return;
    }
    setError(null);
    const r = new FileReader();
    // Held as a data URL in the draft for preview; it's uploaded to storage and
    // swapped for a hosted URL on Publish (and discarded with the draft if cancelled).
    r.onload = () => onChange("logoImg", r.result as string);
    r.readAsDataURL(f);
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="space-y-[7px]">
        <Label htmlFor="logo-wordmark">Wordmark text</Label>
        <Input
          id="logo-wordmark"
          value={theme.logoText}
          onChange={(e) => onChange("logoText", e.target.value)}
          placeholder="brand name"
        />
      </div>
      <div className="space-y-[7px]">
        <Label>Logo image</Label>
        <div className="flex items-center gap-2.5">
          <div className="grid h-11 w-24 shrink-0 place-items-center overflow-hidden rounded-[10px] border border-border bg-field">
            {theme.logoImg ? (
              <img
                src={theme.logoImg}
                alt="logo"
                className="max-h-[78%] max-w-[84%] object-contain"
              />
            ) : (
              <ImageIcon className="size-5 text-text-3" />
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={onFile}
            className="hidden"
          />
          <Button variant="subtle" size="sm" onClick={() => fileRef.current?.click()}>
            <Upload className="size-4" /> Upload
          </Button>
          {theme.logoImg && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setError(null);
                onChange("logoImg", null);
              }}
            >
              Remove
            </Button>
          )}
        </div>
        {error ? (
          <p className="text-[11.5px] text-[#ff8a8a]">{error}</p>
        ) : (
          <p className="text-[11.5px] text-text-3">PNG or SVG, up to 1 MB. Overrides the wordmark when set.</p>
        )}
      </div>
    </div>
  );
}
